/**
 * Pure validation helpers. The backend runs these on every request; the
 * frontend runs the same functions to give immediate feedback. Sharing the
 * implementation guarantees the two never disagree about what is valid.
 */
import type { FieldErrors } from './types/api.ts';
import { PAGE_LIMITS } from './types/page.ts';
import type { Affiliation, ProfileVisibility, SocialLink, SpokenLanguage } from './types/user.ts';
import {
  PAGE_SLUG_PATTERN,
  RESERVED_PAGE_SLUGS,
  ALLOWED_LINK_SCHEMES,
  LANGUAGE_PROFICIENCIES,
  LIMITS,
  PROFILE_LIMITS,
  PROFILE_VISIBILITIES,
  RESERVED_USERNAMES,
  USERNAME_PATTERN,
  isValidCountry,
  isValidInterest,
  isValidLanguage,
} from './constants.ts';

export class ValidationBag {
  private readonly fields: FieldErrors = {};

  add(field: string, message: string): this {
    (this.fields[field] ??= []).push(message);
    return this;
  }

  get ok(): boolean {
    return Object.keys(this.fields).length === 0;
  }

  get errors(): FieldErrors {
    return this.fields;
  }
}

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Strips characters that have no business in stored text: C0/C1 controls
 * (except tab/newline), zero-width joiners and bidi overrides used for spoofing.
 */
export function sanitizeText(value: string): string {
  return value
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '')
    .replace(/[​-‏‪-‮⁦-⁩﻿]/g, '')
    .trim();
}

/** Collapses runs of blank lines so a message cannot be 4000 newlines. */
export function normalizeMessage(value: string): string {
  return sanitizeText(value).replace(/\n{3,}/g, '\n\n');
}

export function isValidEmail(value: string): boolean {
  if (value.length > LIMITS.emailMax) return false;
  // Intentionally conservative: one @, no spaces, a dotted domain.
  return /^[^\s@]{1,64}@[^\s@.]+(\.[^\s@.]+)+$/.test(value);
}

/* -------------------------------------------------------------------------- */
/* Field validators                                                            */
/* -------------------------------------------------------------------------- */

export function validateUsername(raw: string, bag: ValidationBag, field = 'username'): string {
  const username = raw.trim().toLowerCase();
  if (username.length < LIMITS.usernameMin || username.length > LIMITS.usernameMax) {
    bag.add(field, `Username must be between ${LIMITS.usernameMin} and ${LIMITS.usernameMax} characters.`);
  } else if (!USERNAME_PATTERN.test(username)) {
    bag.add(field, 'Username may only contain lowercase letters, numbers and underscores, and must start with a letter or number.');
  } else if (RESERVED_USERNAMES.has(username)) {
    bag.add(field, 'That username is reserved.');
  }
  return username;
}

export function validateEmail(raw: string, bag: ValidationBag, field = 'email'): string {
  const email = raw.trim().toLowerCase();
  if (!isValidEmail(email)) bag.add(field, 'Enter a valid email address.');
  return email;
}

/**
 * Length is the primary defence (NIST SP 800-63B): long passphrases beat
 * composition rules. We reject only what is short or obviously guessable.
 */
/**
 * The password rules, one boolean each.
 *
 * `validatePassword` is built on top of this rather than repeating the tests,
 * so a sign-up form can show a live checklist that cannot drift from what the
 * server will actually accept. Adding a rule here adds it to both.
 */
export interface PasswordRules {
  /** Between `passwordMin` and `passwordMax` characters. */
  length: boolean;
  /** Not the same character repeated. */
  varied: boolean;
  /** Not one of the passwords guessed first. */
  uncommon: boolean;
}

export function passwordRules(password: string): PasswordRules {
  // Nothing typed yet satisfies nothing, rather than vacuously passing the
  // two rules a zero-length string cannot break.
  if (password.length === 0) return { length: false, varied: false, uncommon: false };
  return {
    length: password.length >= LIMITS.passwordMin && password.length <= LIMITS.passwordMax,
    varied: !/^(.)\1+$/.test(password),
    uncommon: !COMMON_PASSWORDS.has(password.toLowerCase()),
  };
}

export function validatePassword(password: string, bag: ValidationBag, field = 'password'): string {
  const rules = passwordRules(password);
  if (!rules.length) {
    bag.add(
      field,
      password.length > LIMITS.passwordMax
        ? `Password must be at most ${LIMITS.passwordMax} characters.`
        : `Password must be at least ${LIMITS.passwordMin} characters.`,
    );
  } else if (!rules.varied) {
    bag.add(field, 'Password is too simple.');
  } else if (!rules.uncommon) {
    bag.add(field, 'That password is too common. Choose something harder to guess.');
  }
  return password;
}

const COMMON_PASSWORDS: ReadonlySet<string> = new Set([
  'password', 'password1', 'password123', 'passw0rd123', '1234567890',
  '12345678901', '123456789012', 'qwertyuiop', 'qwerty123456', 'iloveyou123',
  'letmein123', 'welcome123', 'admin123456', 'abc123456789', 'football123',
]);

/**
 * A date of birth, and whether the person is old enough to be here.
 *
 * Takes `YYYY-MM-DD` and nothing else: a free-form date is a locale argument
 * nobody wins — 03/04/2009 is two different birthdays depending on who typed
 * it — and the browser's own date input already produces this shape.
 *
 * The age is computed by comparing the date parts rather than by dividing a
 * difference in milliseconds, because a year is not a fixed number of them:
 * somebody born on 29 February, or a run that straddles a leap day, comes out
 * a day wrong often enough to matter on a boundary that decides whether an
 * account may exist at all.
 */
export function validateBirthdate(raw: string, bag: ValidationBag, field = 'birthdate'): string {
  const value = raw.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    bag.add(field, 'Enter a date of birth as YYYY-MM-DD.');
    return value;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  // Rejects 2009-02-30 and friends: Date rolls those over silently, so the
  // only way to know it was real is to read the parts back.
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    bag.add(field, 'That is not a real date.');
    return value;
  }

  const age = ageOn(value, new Date());
  if (age === null || age < 0) {
    bag.add(field, 'A date of birth cannot be in the future.');
  } else if (age < LIMITS.minAgeYears) {
    bag.add(field, `You must be at least ${LIMITS.minAgeYears} to use TalkVibe.`);
  } else if (age > LIMITS.maxAgeYears) {
    bag.add(field, 'Check the year — that date is further back than anybody alive.');
  }
  return value;
}

/**
 * Completed years between a `YYYY-MM-DD` birth date and a moment.
 *
 * Whole years only, counted by calendar: somebody turns fifteen on their
 * birthday, not on the 5,479th day.
 */
export function ageOn(birthdate: string, on: Date): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthdate.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  let age = on.getUTCFullYear() - year;
  const beforeBirthday =
    on.getUTCMonth() + 1 < month || (on.getUTCMonth() + 1 === month && on.getUTCDate() < day);
  if (beforeBirthday) age -= 1;
  return age;
}

export function validateCountry(raw: string, bag: ValidationBag, field = 'country'): string {
  const country = raw.trim().toUpperCase();
  if (!isValidCountry(country)) bag.add(field, 'Select a valid country.');
  return country;
}

export function validateLanguage(raw: string, bag: ValidationBag, field = 'language'): string {
  const language = raw.trim();
  if (!isValidLanguage(language)) bag.add(field, 'Select a supported language.');
  return language;
}

export function validateDisplayName(raw: string, bag: ValidationBag, field = 'displayName'): string {
  const name = sanitizeText(raw).replace(/\s+/g, ' ');
  if (name.length < LIMITS.displayNameMin || name.length > LIMITS.displayNameMax) {
    bag.add(field, `Display name must be between ${LIMITS.displayNameMin} and ${LIMITS.displayNameMax} characters.`);
  }
  return name;
}

export function validateBio(raw: string | null, bag: ValidationBag, field = 'bio'): string | null {
  if (raw === null) return null;
  const bio = sanitizeText(raw);
  if (bio.length === 0) return null;
  if (bio.length > LIMITS.bioMax) {
    bag.add(field, `Bio must be at most ${LIMITS.bioMax} characters.`);
  }
  return bio;
}

/**
 * Jobs, or places studied.
 *
 * Replace-all: the caller sends the whole ordered list and gets back a clean
 * one, in the same order. Entries that are nothing but whitespace are dropped
 * rather than refused — an empty row in a form that lets somebody add rows is
 * a row they stopped filling in, not a mistake worth an error message. The
 * database cannot catch those: `organisation VARCHAR(120) NOT NULL` accepts
 * the empty string quite happily, which was confirmed by trying it.
 */
export function validateAffiliations(
  raw: unknown,
  bag: ValidationBag,
  field = 'work',
): Affiliation[] {
  if (!Array.isArray(raw)) {
    bag.add(field, 'Must be a list.');
    return [];
  }
  if (raw.length > LIMITS.affiliationsMax) {
    bag.add(field, `Add at most ${LIMITS.affiliationsMax}.`);
    return [];
  }

  // A year somebody is working towards is a real answer — a degree finishing
  // in two years, a contract running to the end of the next. Ten years of
  // slack accepts those and still refuses 2190 as the typo it is.
  const ceiling = new Date().getUTCFullYear() + 10;
  const out: Affiliation[] = [];

  for (const [index, entry] of raw.entries()) {
    if (typeof entry !== 'object' || entry === null) {
      bag.add(field, `Entry ${index + 1} is not an object.`);
      continue;
    }
    const source = entry as Record<string, unknown>;

    const organisation = sanitizeText(String(source['organisation'] ?? ''));
    // Dropped, not refused. See the note above.
    if (organisation.length === 0) continue;
    if (organisation.length > LIMITS.organisationMax) {
      bag.add(field, `Names must be at most ${LIMITS.organisationMax} characters.`);
      continue;
    }

    const rawRole = source['role'];
    let role: string | null = null;
    if (typeof rawRole === 'string') {
      const cleaned = sanitizeText(rawRole);
      if (cleaned.length > LIMITS.affiliationRoleMax) {
        bag.add(field, `Roles must be at most ${LIMITS.affiliationRoleMax} characters.`);
        continue;
      }
      role = cleaned.length === 0 ? null : cleaned;
    }

    const year = (key: string): number | null => {
      const value = source[key];
      if (value === null || value === undefined || value === '') return null;
      const number = Number(value);
      // `Number('')` is 0 and `Number('x')` is NaN, so both are checked rather
      // than trusting the cast.
      if (!Number.isInteger(number) || number < LIMITS.affiliationMinYear || number > ceiling) {
        bag.add(field, `Years must be between ${LIMITS.affiliationMinYear} and ${ceiling}.`);
        return null;
      }
      return number;
    };

    const fromYear = year('fromYear');
    const toYear = year('toYear');
    if (fromYear !== null && toYear !== null && toYear < fromYear) {
      bag.add(field, 'An end year cannot be before its start year.');
      continue;
    }

    out.push({ organisation, role, fromYear, toYear });
  }

  return out;
}

export function validateInterests(raw: unknown, bag: ValidationBag, field = 'interests'): string[] {
  if (!Array.isArray(raw)) {
    bag.add(field, 'Interests must be a list.');
    return [];
  }
  if (raw.length > LIMITS.interestsMax) {
    bag.add(field, `Choose at most ${LIMITS.interestsMax} interests.`);
    return [];
  }
  const seen = new Set<string>();
  for (const entry of raw) {
    if (typeof entry !== 'string') {
      bag.add(field, 'Interests must be a list of strings.');
      return [];
    }
    const slug = entry.trim().toLowerCase();
    if (!isValidInterest(slug)) {
      bag.add(field, `Unknown interest: ${slug.slice(0, LIMITS.interestMax)}`);
      return [];
    }
    seen.add(slug);
  }
  return [...seen];
}

/* -------------------------------------------------------------------------- */
/* Profile extras                                                              */
/* -------------------------------------------------------------------------- */

export function validateVisibility(raw: unknown, bag: ValidationBag, field = 'visibility'): ProfileVisibility {
  if (typeof raw === 'string' && (PROFILE_VISIBILITIES as readonly string[]).includes(raw)) {
    return raw as ProfileVisibility;
  }
  bag.add(field, 'Choose public, friends or private.');
  return 'public';
}

/**
 * Validates an IANA time zone.
 *
 * Checked against the runtime's own database rather than a hardcoded list, so
 * it stays correct as zones are added or renamed.
 */
export function validateTimezone(raw: string | null, bag: ValidationBag, field = 'timezone'): string | null {
  if (raw === null) return null;
  const zone = raw.trim();
  if (zone.length === 0) return null;
  if (zone.length > PROFILE_LIMITS.timezoneMax) {
    bag.add(field, 'That time zone name is too long.');
    return null;
  }
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return zone;
  } catch {
    bag.add(field, 'Unknown time zone.');
    return null;
  }
}

export function validateSpokenLanguages(
  raw: unknown,
  bag: ValidationBag,
  field = 'languages',
): SpokenLanguage[] {
  if (!Array.isArray(raw)) {
    bag.add(field, 'Languages must be a list.');
    return [];
  }
  if (raw.length > PROFILE_LIMITS.maxSpokenLanguages) {
    bag.add(field, `Choose at most ${PROFILE_LIMITS.maxSpokenLanguages} languages.`);
    return [];
  }

  const seen = new Map<string, SpokenLanguage>();
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) {
      bag.add(field, 'Each language needs a code and a proficiency.');
      return [];
    }
    const { language, proficiency } = entry as { language?: unknown; proficiency?: unknown };
    if (typeof language !== 'string' || !isValidLanguage(language)) {
      bag.add(field, 'Unsupported language.');
      return [];
    }
    if (
      typeof proficiency !== 'string' ||
      !(LANGUAGE_PROFICIENCIES as readonly string[]).includes(proficiency)
    ) {
      bag.add(field, 'Proficiency must be learning, conversational, fluent or native.');
      return [];
    }
    // Later entries win, so a duplicated code cannot produce two rows.
    seen.set(language, { language, proficiency: proficiency as SpokenLanguage['proficiency'] });
  }
  return [...seen.values()];
}

/**
 * Validates social links.
 *
 * The scheme check is the important part: these URLs are rendered as anchors,
 * and `javascript:` or `data:` in an href is a stored XSS. Only http and https
 * are accepted, and the parsed URL is returned rather than the raw string so
 * nothing odd survives normalisation.
 */
export function validateSocialLinks(raw: unknown, bag: ValidationBag, field = 'socialLinks'): SocialLink[] {
  if (!Array.isArray(raw)) {
    bag.add(field, 'Links must be a list.');
    return [];
  }
  if (raw.length > PROFILE_LIMITS.maxSocialLinks) {
    bag.add(field, `Add at most ${PROFILE_LIMITS.maxSocialLinks} links.`);
    return [];
  }

  const links: SocialLink[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) {
      bag.add(field, 'Each link needs a label and a URL.');
      return [];
    }
    const { label, url } = entry as { label?: unknown; url?: unknown };

    const cleanLabel = typeof label === 'string' ? sanitizeText(label) : '';
    if (cleanLabel.length === 0 || cleanLabel.length > PROFILE_LIMITS.socialLinkLabelMax) {
      bag.add(field, `Each link needs a label of at most ${PROFILE_LIMITS.socialLinkLabelMax} characters.`);
      return [];
    }
    if (typeof url !== 'string' || url.length > PROFILE_LIMITS.socialLinkUrlMax) {
      bag.add(field, 'That link is not a valid URL.');
      return [];
    }

    let parsed: URL;
    try {
      parsed = new URL(url.trim());
    } catch {
      bag.add(field, 'That link is not a valid URL.');
      return [];
    }
    if (!ALLOWED_LINK_SCHEMES.has(parsed.protocol)) {
      bag.add(field, 'Links must start with http:// or https://');
      return [];
    }

    links.push({ label: cleanLabel, url: parsed.toString() });
  }
  return links;
}

export function validateMessageContent(raw: string, bag: ValidationBag, field = 'content'): string {
  const content = normalizeMessage(raw);
  if (content.length === 0) {
    bag.add(field, 'Message cannot be empty.');
  } else if (content.length > LIMITS.messageMax) {
    bag.add(field, `Message must be at most ${LIMITS.messageMax} characters.`);
  }
  return content;
}

/**
 * The same, for a message that carries a photo.
 *
 * Empty is allowed here and nowhere else: a picture with no caption is an
 * ordinary thing to send. The length limit and the sanitiser still apply — what
 * changes is only whether nothing is an answer.
 */
export function validateOptionalMessageContent(
  raw: string,
  bag: ValidationBag,
  field = 'content',
): string {
  const content = normalizeMessage(raw);
  if (content.length > LIMITS.messageMax) {
    bag.add(field, `Message must be at most ${LIMITS.messageMax} characters.`);
  }
  return content;
}

/* -------------------------------------------------------------------------- */
/* Pages                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Validates a page slug.
 *
 * Stricter than a username because this is a public URL that will be printed,
 * typed and shared: lower-case only so the same page has one address, single
 * hyphens only so `a--b` and `a-b` are not two different pages, and no
 * collision with a route the app already owns.
 */
export function validatePageSlug(raw: string, bag: ValidationBag, field = 'slug'): string {
  const slug = raw.trim().toLowerCase();

  if (slug.length < PAGE_LIMITS.slugMin || slug.length > PAGE_LIMITS.slugMax) {
    bag.add(
      field,
      `Use between ${PAGE_LIMITS.slugMin} and ${PAGE_LIMITS.slugMax} characters.`,
    );
    return slug;
  }
  if (!PAGE_SLUG_PATTERN.test(slug)) {
    bag.add(field, 'Use lower-case letters, numbers and single hyphens.');
    return slug;
  }
  if (RESERVED_PAGE_SLUGS.has(slug)) {
    bag.add(field, 'That address is reserved.');
  }
  return slug;
}

export function validatePageName(raw: string, bag: ValidationBag, field = 'name'): string {
  const name = sanitizeText(raw);
  if (name.length === 0 || name.length > PAGE_LIMITS.nameMax) {
    bag.add(field, `Give the page a name of up to ${PAGE_LIMITS.nameMax} characters.`);
  }
  return name;
}

/** Trimmed to the length a search result will actually show. */
export function validateTagline(raw: string, bag: ValidationBag, field = 'tagline'): string {
  const tagline = sanitizeText(raw);
  if (tagline.length > PAGE_LIMITS.taglineMax) {
    bag.add(field, `Keep it under ${PAGE_LIMITS.taglineMax} characters.`);
  }
  return tagline;
}

export function validatePostContent(raw: string, bag: ValidationBag, field = 'content'): string {
  const content = sanitizeText(raw);
  if (content.length === 0) {
    bag.add(field, 'Write something first.');
  } else if (content.length > PAGE_LIMITS.postMax) {
    bag.add(field, `Keep a post under ${PAGE_LIMITS.postMax} characters.`);
  }
  return content;
}

export function validateCommentContent(raw: string, bag: ValidationBag, field = 'content'): string {
  const content = sanitizeText(raw);
  if (content.length === 0) {
    bag.add(field, 'Write something first.');
  } else if (content.length > PAGE_LIMITS.commentMax) {
    bag.add(field, `Keep a comment under ${PAGE_LIMITS.commentMax} characters.`);
  }
  return content;
}
